import React from "react"

import { useTranslation } from "react-i18next"

import { Modal } from "components/modals/Modal"

import { AdminBonus2 } from "services/bonus/type"

import { getSectionPresets } from "../../helpers/bonus2.helpers"
import { stakeIssueMessage } from "../../helpers/spin-stake.helpers"

import { PresetRail, Tooltip } from "./bonus-form-ui"
import { renderSectionBody, SectionId, sectionMeta } from "./BonusFormSections"
import { TypeChangeConfirm } from "./TypeChangeConfirm"
import { useBonusForm } from "./useBonusForm"

interface BonusFormProps {
  bonus?: AdminBonus2
  /** Seed for a duplicate — prefills every field but still SAVES AS NEW. */
  duplicateFrom?: AdminBonus2
  tenant: string
  onClose: () => void
  onSave: () => void
}

export const BonusForm: React.FC<BonusFormProps> = ({ bonus, duplicateFrom, tenant, onClose, onSave }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const ctx = useBonusForm({ bonus, duplicateFrom, tenant, onClose, onSave })
  const {
    formData,
    handleChange,
    applyPreset,
    handleSubmit,
    loading,
    openSection,
    setOpenSection,
    summaryChips,
    configWarnings,
    isEdit,
    isDuplicate,
    saveIssues,
    pendingType,
    confirmTypeChange,
    cancelTypeChange
  } = ctx

  return (
    <Modal open={true} handler={onClose}>
      {/* Wider modal with a STABLE height (h-[85vh], not max-h) so it doesn't grow/shrink as
          sections expand and collapse — the content region scrolls instead. The modal-box
          itself does NOT scroll (overflow-hidden keeps the rounded corners intact); only the
          middle sections region scrolls, with right padding so the scrollbar never overlaps
          the corner radius. */}
      <div className="modal-box relative !max-w-none w-[90vw] h-[85vh] p-0 overflow-hidden flex flex-col">
        <div className="shrink-0 bg-base-100 px-6 pt-6 pb-4 border-b border-[rgba(132,0,168,0.3)]">
          <h2 className="text-2xl font-bold dark:text-white">
            {t(isEdit ? "bonus2.form.editTitle" : isDuplicate ? "bonus2.form.duplicateTitle" : "bonus2.form.createTitle")}
          </h2>
          <p className="mt-1 text-sm text-gray-400">{t(isDuplicate ? "bonus2.form.duplicateSubtitle" : "bonus2.form.subtitle")}</p>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5 pr-4 space-y-3">
            {sectionMeta.map((s, index) => {
              const open = openSection === s.id
              return (
                <section
                  key={s.id}
                  className={`rounded-xl border transition-all ${
                    open ? "border-[rgba(169,78,206,0.7)] bg-[rgba(132,0,168,0.04)]" : "border-[rgba(132,0,168,0.25)]"
                  }`}
                >
                  <button
                    type="button"
                    aria-expanded={open}
                    onClick={() => setOpenSection(open ? ("" as SectionId) : s.id)}
                    className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left"
                  >
                    <span className="flex items-center gap-3">
                      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[rgba(169,78,206,1)] text-sm font-bold text-white">
                        {index + 1}
                      </span>
                      <span>
                        <span className="block font-semibold dark:text-white">{t(s.title)}</span>
                        <span className="block text-xs text-gray-400">{t(s.summary)}</span>
                      </span>
                    </span>
                    <span className={`text-gray-400 transition-transform ${open ? "rotate-0" : "-rotate-90"}`}>⌄</span>
                  </button>
                  {open && (
                    <div className="border-t border-[rgba(132,0,168,0.25)] p-4">
                      {(() => {
                        const presets = getSectionPresets(s.id, formData, t)
                        // Cap the open body height so it fits within the modal; the fields (and
                        // the preset rail) scroll INTERNALLY instead of stretching the section.
                        if (presets.length === 0) {
                          return <div className="max-h-[42vh] overflow-y-auto pr-1">{renderSectionBody(s.id, ctx)}</div>
                        }
                        return (
                          <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
                            <div className="min-w-0 flex-1 max-h-[42vh] overflow-y-auto pr-1">{renderSectionBody(s.id, ctx)}</div>
                            <div className="lg:w-72 lg:shrink-0 lg:border-l lg:border-[rgba(132,0,168,0.2)] lg:pl-4">
                              <div className="max-h-[42vh] overflow-y-auto pr-1">
                                <PresetRail presets={presets} onApply={applyPreset} />
                              </div>
                            </div>
                          </div>
                        )
                      })()}
                    </div>
                  )}
                </section>
              )
            })}
          </div>

          {/* Pinned footer — outside the scroll region so it never scrolls away and the
              scrollbar stays clear of the modal's rounded corners. */}
          <div className="shrink-0 border-t border-[rgba(132,0,168,0.3)] bg-base-100 px-6 py-3">
            {/* SOFT-736 — why the last save was refused. Nothing was written, and each issue also
                marks the field it belongs to (currency row / games); this strip is the one place
                that shows them all at once, including the ones with no field of their own. */}
            {saveIssues.length > 0 && (
              <div className="mb-3 rounded-lg border border-rose-500/50 bg-rose-500/10 px-3 py-2">
                <div className="text-xs font-semibold text-rose-300">{t("bonus2.form.notSavedStakeRefused")}</div>
                <ul className="mt-1 space-y-0.5">
                  {saveIssues.map((issue, i) => (
                    <li key={`${issue.code}-${issue.coin ?? ""}-${issue.game_id ?? i}`} className="text-xs leading-snug text-rose-200">
                      • {stakeIssueMessage(issue, t)}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Always-visible config summary + warnings. */}
            <div className="mb-3 flex items-start gap-3">
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                {summaryChips.map((c) => (
                  <span
                    key={c}
                    className="whitespace-nowrap rounded-full border border-[rgba(132,0,168,0.3)] bg-[rgba(132,0,168,0.08)] px-2 py-0.5 text-xs text-gray-300"
                  >
                    {c}
                  </span>
                ))}
              </div>
              {/* Warnings — small yellow dots, each with a hover tooltip. */}
              {configWarnings.length > 0 && (
                <div className="flex shrink-0 items-center gap-1.5">
                  <span className="text-xs text-amber-300">{t("bonus2.form.toReview", { count: configWarnings.length })}</span>
                  {configWarnings.map((wtext, i) => (
                    <Tooltip key={i} text={wtext} border="rgba(245,158,11,0.5)">
                      <span className="h-3 w-3 cursor-help rounded-full bg-amber-400 shadow-[0_0_0_2px_rgba(251,191,36,0.25)]" />
                    </Tooltip>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-3">
              <label className="flex cursor-pointer items-center gap-3">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => handleChange("active", e.target.checked)}
                  className="checkbox checkbox-primary"
                />
                <span className="font-medium dark:text-white">{t("bonus2.form.activeLabel")}</span>
              </label>
              <div className="flex gap-3">
                <button type="button" onClick={onClose} className="btn btn-secondary">
                  {t("bonus2.form.cancel")}
                </button>
                <button type="submit" disabled={loading} className="btn btn-primary">
                  {loading ? t("bonus2.form.saving") : isEdit ? t("bonus2.form.update") : t("bonus2.form.create")}
                </button>
              </div>
            </div>
          </div>
        </form>

        {pendingType && (
          <TypeChangeConfirm
            type={pendingType.type}
            effects={pendingType.effects}
            onConfirm={confirmTypeChange}
            onCancel={cancelTypeChange}
          />
        )}
      </div>
    </Modal>
  )
}
